import React, { useEffect } from 'react';
import Link from 'next/link';
import { Card } from '@havenengineering/module-shared-owners-ui/dist/components/Card';
import { SectionHeader } from '@havenengineering/module-shared-owners-ui/dist/components/SectionHeader';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import { swrFetcher } from '../../helpers/fetch';
import { pageView } from '../../helpers/googleTag';
import styles from '../../styles/pages/get-in-touch.module.scss';
import { Park } from '../../types/parks';

const GetInTouch = () => {
  const { loggedInUser, activeAccount, gtmPageViewAuthInfo } = useAuthContext();

  useEffect(() => {
    pageView(
      'dashboard',
      '/ga-virtual/owners-account/get-in-touch',
      'owners account',
      gtmPageViewAuthInfo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const park = useSWRImmutable<Park>(
    activeAccount?.parkCode && `/parks/${activeAccount.parkCode}`,
    swrFetcher
  ).data;

  const { data } = useSWR<{ balance: number }>(
    activeAccount && `/ledger/balance?accountId=${activeAccount?.accountID}`,
    swrFetcher
  );

  const surveyUrlString = (
    activeAccount: UserAccount,
    loggedInUser: User,
    data?: { balance: number }
  ) => {
    const queryParams = new URLSearchParams({
      OWNER_ID: `${activeAccount.accountID}`,
      AccountNo: activeAccount.accountNo,
      Account_Balance: `${data?.balance}` || '0',
      ParkName: activeAccount.parkName,
      CommArea: activeAccount.areaName,
      PitchNo: activeAccount.pitchNumber,
      Park: activeAccount.parkCode,
      Pitch_Status: activeAccount.pitchStatusDesc,
      VAN_YEAR: `${activeAccount.vanYear}`,
      DATE_SOLD: activeAccount.latestSaleCompletionDate,
      SALUTATION: loggedInUser.salutation || loggedInUser.name,
      Email: loggedInUser.email,
    });

    return `https://haven.eu.qualtrics.com/jfe/form/SV_bI3ufBpKJBZE2fb?${queryParams.toString()}`;
  };

  return (
    <>
      <SectionHeader title="Get in touch" variant="borderBottom" />
      <div className={styles.cardContainer}>
        <a
          href={`tel:${park?.phoneNumber?.replace(/\s+/g, '')}`}
          data-testid="park-enquiries-card"
        >
          <Card
            variant="alternate"
            icon={'https://www.haven.com/assets/icons/phone.svg'}
            title="Park enquiries"
            description={`For Park enquiries that aren’t answered in our FAQs please call the number displayed below and one of our friendly park team will assist you. They handle questions to do with anything on Park such as Activities and WiFi. Please call ${park?.phoneNumber}.`}
          />
        </a>
        <Link href="/account-enquiries" legacyBehavior>
          <a data-testid="account-enquiries-card">
            <Card
              variant="alternate"
              icon="/assets/icon-magnifier.svg"
              title="Account enquiries"
              description="For Account enquiries that aren’t answered in our FAQs please click here and one our Central Team will get back to you within 14 days. They answer queries about things like Insurance Cover, Billing and Change of Address."
            />
          </a>
        </Link>
        {loggedInUser && activeAccount ? (
          <a
            href={surveyUrlString(activeAccount, loggedInUser, data)}
            target="_blank"
            rel="noreferrer"
            data-testid="feedback-card"
          >
            <Card
              variant="alternate"
              icon="/assets/icon-feedback.svg"
              title="Feedback and Complaints"
              description="We're always listening and love to hear your feedback. Click here to give your feedback or if you need to make a complaint."
            />
          </a>
        ) : null}
        <Link href="/request-funds/refund" legacyBehavior>
          <a data-testid="request-refund-card">
            <Card
              variant="alternate"
              icon="/assets/withdraw.svg"
              title="Refund Application"
              description="If you're in exceptional circumstances, you can apply for credit on your account to be paid into your bank account."
            />
          </a>
        </Link>
      </div>
    </>
  );
};

export const getStaticProps = () => ({
  props: {
    pageTitle: 'Get in touch',
    availableForGuests: true,
  },
  revalidate: 60 * 30,
});

export default GetInTouch;
